import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { BRAND } from "@/lib/data";
import { fetchMedia, FILE_URL } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

export const LogoSection = () => {
  const [logoImage, setLogoImage] = useState(null);
  const [logoVideo, setLogoVideo] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    fetchMedia("logo")
      .then((data) => {
        const img = data.find((m) => m.media_type === "image");
        const vid = data.find((m) => m.media_type === "video");
        if (img) setLogoImage(FILE_URL(img.url));
        if (vid) setLogoVideo(FILE_URL(vid.url));
      })
      .catch(() => {});
  }, []);

  return (
    <section id="brand" data-testid="logo-section" className="relative bg-[#050505] py-24 md:py-36 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="label mb-5">The Brand</p>
          <h2 className="font-display uppercase text-white leading-[0.9] text-5xl sm:text-7xl">
            One mark.
            <br />
            One <span className="text-green glow-green">mission</span>.
          </h2>
          <p className="font-sub text-lg text-zinc-400 mt-6 font-light max-w-md">
            The Mask Giver logo stands for showing up in the shadows and handing over hope.
            Watch the story behind the mark.
          </p>
          {logoVideo ? (
            <button
              data-testid="logo-play-btn"
              onClick={() => setPlaying(true)}
              className="btn-fill mt-8 border border-green text-green font-sub font-semibold px-8 py-3.5 rounded-full flex items-center gap-3 box-glow"
            >
              <Play size={18} /> Play intro video
            </button>
          ) : (
            <p className="font-sub text-sm text-zinc-600 mt-8">
              Intro video coming soon — upload it from the{" "}
              <a href="/admin" className="text-green link-underline">admin studio</a>.
            </p>
          )}
        </motion.div>

        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <button
            onClick={() => logoVideo && setPlaying(true)}
            data-testid="logo-visual"
            className={`group relative w-full aspect-square md:aspect-video border border-[#1a1a1a] bg-black overflow-hidden ${logoVideo ? "cursor-pointer" : "cursor-default"}`}
          >
            {logoImage ? (
              <img src={logoImage} alt="Mask Giver logo" className="w-full h-full object-contain p-10" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <span className="font-display text-[26vw] md:text-[12rem] leading-none">
                  <span className="text-[#141414]">M</span>
                  <span className="text-green glow-green">G</span>
                </span>
                <span className="font-display uppercase tracking-[0.3em] text-green text-lg md:text-2xl -mt-4">
                  {BRAND.name}
                </span>
              </div>
            )}
            {logoVideo && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                <span className="bg-green text-black rounded-full p-5 group-hover:scale-110 transition-transform">
                  <Play size={26} />
                </span>
              </span>
            )}
          </button>
        </motion.div>
      </div>

      <AnimatePresence>
        {playing && logoVideo && (
          <motion.div
            className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPlaying(false)}
            data-testid="logo-video-modal"
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-green"
              onClick={() => setPlaying(false)}
              data-testid="logo-video-close"
            >
              <X size={30} />
            </button>
            <video
              src={logoVideo}
              className="max-w-4xl w-full max-h-[80vh]"
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
